import type { Appointment } from '@medplum/fhirtypes';

/**
 * FHIR Appointment.status values surfaced in the provider UI.
 * @see http://hl7.org/fhir/R4/valueset-appointmentstatus.html
 */
export const CLINICAL_APPOINTMENT_FHIR_STATUSES = [
  'proposed',
  'booked',
  'fulfilled',
  'cancelled',
  'noshow',
] as const satisfies readonly Appointment['status'][];

export type ClinicalAppointmentFhirStatus = (typeof CLINICAL_APPOINTMENT_FHIR_STATUSES)[number];

export type AppointmentStatusOption = {
  value: ClinicalAppointmentFhirStatus;
  label: string;
  description: string;
};

/**
 * Central mapping: persisted / wire value = FHIR code, UI = label.
 */
export const APPOINTMENT_STATUS_OPTIONS: ReadonlyArray<AppointmentStatusOption> = [
  {
    value: 'proposed',
    label: 'Requested',
    description: 'Requested but not yet confirmed, e.g. client portal request',
  },
  {
    value: 'booked',
    label: 'Scheduled',
    description: 'Confirmed and on the calendar',
  },
  {
    value: 'fulfilled',
    label: 'Completed',
    description: 'Session completed successfully',
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
    description: 'Appointment was cancelled; see cancellationReason when present',
  },
  {
    value: 'noshow',
    label: 'No-Show',
    description: 'Client did not attend',
  },
] as const;

const CORE_LABEL_BY_FHIR: Record<string, string> = {
  proposed: 'Requested',
  booked: 'Scheduled',
  fulfilled: 'Completed',
  cancelled: 'Cancelled',
  noshow: 'No-Show',
};

const OPTION_BY_FHIR: Record<string, string> = {
  ...CORE_LABEL_BY_FHIR,
  arrived: 'Scheduled',
  'checked-in': 'Scheduled',
  pending: 'Scheduled',
  waitlist: 'Scheduled',
};

const LEGACY_UI_LABEL: Record<string, string> = {
  show: 'Completed',
  scheduled: 'Scheduled',
};

/**
 * Map any known status string (FHIR, legacy) to the canonical app-facing label
 * for appointment **session** outcome (not documentation workflow).
 */
export function formatFhirOrLegacyAppointmentStatus(
  status: string | null | undefined,
  fallback: string = '—',
): string {
  if (status == null || status === '') return fallback;
  const n = status.toLowerCase().replace(/[\s_]+/g, '-');
  if (n === 'no-show' || n === 'no_show') {
    return CORE_LABEL_BY_FHIR.noshow;
  }
  if (OPTION_BY_FHIR[n]) {
    return OPTION_BY_FHIR[n]!;
  }
  if (n === 'entered' || n === 'entered-in-error') return 'Cancelled';
  if (LEGACY_UI_LABEL[n]) {
    // Map old label to new: show → Completed
    const mapped = LEGACY_UI_LABEL[n];
    return mapped === 'Scheduled' ? 'Scheduled' : mapped;
  }
  // Title Case legacy e.g. "Show"
  if (n === 'show') return 'Completed';
  return status;
}

/**
 * @deprecated Use {@link formatFhirOrLegacyAppointmentStatus}; kept for incremental refactors.
 */
export function getAppointmentStatusLabel(
  status: string | null | undefined,
  fallback: string = '—',
): string {
  return formatFhirOrLegacyAppointmentStatus(status, fallback);
}

export const COMPLETED_APPOINTMENT_SESSION_STATUSES = new Set<string>([
  'fulfilled',
  'cancelled',
  'noshow',
  'no-show',
  'entered-in-error',
]);

/**
 * For dropdowns: unknown / legacy string → a valid clinical FHIR status.
 */
export function coerceClinicalAppointmentFhirStatus(
  s: string | null | undefined,
): ClinicalAppointmentFhirStatus {
  if (s && (CLINICAL_APPOINTMENT_FHIR_STATUSES as readonly string[]).includes(s)) {
    return s as ClinicalAppointmentFhirStatus;
  }
  return 'booked';
}
